import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, ArrowDown, ArrowUp } from 'lucide-react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@scio3/ui';
import { SORT_DIRECTION, formatModel, formatOs } from '@scio3/shared-types';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import { useMdmDevicesSelectionStore } from '../stores/mdm-devices-selection-store';
import { useMdmDevicesSortStore, type MdmDeviceSortField } from '../stores/mdm-devices-sort-store';
import { useMdmDevicesViewStore } from '../stores/mdm-devices-view-store';
import { DeviceStatusDot } from './DeviceStatusDot';

// Phase 7 T070 — labels come from the shared `@scio3/shared-types/device-labels`
// formatters which take the backend-classified `DeviceClassification`. When an
// older cached device still lacks classification (e.g. an open detail drawer
// during deploy rollover), fall back to an em-dash rather than crashing.
function formatOsSafe(d: MdmDevice): string {
  return d.classification ? formatOs(d.classification) : '—';
}
function formatModelSafe(d: MdmDevice): string {
  return d.classification ? formatModel(d.classification) : (d.model ?? '—');
}

interface ColumnSpec {
  id: string;
  labelKey: string;
  sortField?: MdmDeviceSortField;
  render: (device: MdmDevice) => React.ReactNode;
}

interface Props {
  devices: MdmDevice[];
  onRowClick: (deviceId: string) => void;
}

export function DeviceTable({ devices, onRowClick }: Props) {
  const { t } = useTranslation();
  const columnConfig = useMdmDevicesViewStore((s) => s.columnConfig);
  const selectedIds = useMdmDevicesSelectionStore((s) => s.selectedIds);
  const toggle = useMdmDevicesSelectionStore((s) => s.toggle);
  const selectAll = useMdmDevicesSelectionStore((s) => s.selectAll);
  const clear = useMdmDevicesSelectionStore((s) => s.clear);
  const sortField = useMdmDevicesSortStore((s) => s.sortField);
  const sortDirection = useMdmDevicesSortStore((s) => s.sortDirection);
  const setSort = useMdmDevicesSortStore((s) => s.setSort);

  const allSelected = devices.length > 0 && devices.every((d) => selectedIds.has(d._id));

  const columnMap = useMemo<Record<string, ColumnSpec>>(
    () => ({
      deviceName: {
        id: 'deviceName',
        labelKey: 'Name',
        sortField: 'deviceName',
        render: (d) => <span className="truncate font-medium">{d.deviceName ?? '—'}</span>,
      },
      fleet: {
        id: 'fleet',
        labelKey: 'Fleet',
        render: (d) =>
          d.fleetId ? (
            <span className="truncate text-sm">{d.fleetId}</span>
          ) : (
            <span className="text-sm text-muted-foreground">{t('Default')}</span>
          ),
      },
      os: {
        id: 'os',
        labelKey: 'OS',
        render: (d) => (
          <span
            className="text-sm"
            title={`${d.os ?? ''} ${d.osVersion ?? ''}`.trim() || undefined}
          >
            {formatOsSafe(d)}
          </span>
        ),
      },
      lastHeartBeat: {
        id: 'lastHeartBeat',
        labelKey: 'Last seen',
        // Not sortable — backend DeviceSort only accepts deviceName / creationDate /
        // currentSelectionDate. Column still renders; sorting by Last seen is
        // intentionally disabled until the backend sort contract widens.
        render: (d) => (
          <span className="text-sm text-muted-foreground">
            {d.lastHeartBeat ? safeDistanceToNow(d.lastHeartBeat, { addSuffix: true }, '—') : '—'}
          </span>
        ),
      },
      model: {
        id: 'model',
        labelKey: 'Model',
        // Raw `model` remains available via the browser tooltip for debugging.
        render: (d) => (
          <span className="text-sm" title={d.model ?? undefined}>
            {formatModelSafe(d)}
          </span>
        ),
      },
      platform: {
        id: 'platform',
        labelKey: 'Platform',
        render: (d) => <span className="text-sm">{d.platform ?? '—'}</span>,
      },
      localAppVersion: {
        id: 'localAppVersion',
        labelKey: 'MDM Ver',
        render: (d) => <span className="text-sm">{d.localAppVersion ?? '—'}</span>,
      },
      serialNo: {
        id: 'serialNo',
        labelKey: 'Serial',
        render: (d) => <span className="text-sm">{d.serialNo ?? '—'}</span>,
      },
      UUID: {
        id: 'UUID',
        labelKey: 'UUID',
        render: (d) => (
          <span className="text-xs font-mono text-muted-foreground">{d.UUID ?? '—'}</span>
        ),
      },
      // Staff-only: VPN IP from mdm-devices left-join. Never expose this on
      // apps/web — the backend `StaffDevice` type is the only way to obtain it.
      vpnIp: {
        id: 'vpnIp',
        labelKey: 'VPN IP',
        render: (d) => (
          <span className="text-xs font-mono text-muted-foreground">{d.vpnIp ?? '—'}</span>
        ),
      },
      tags: {
        id: 'tags',
        labelKey: 'Tags',
        render: (d) => (
          <span className="text-xs text-muted-foreground">
            {d.tags && d.tags.length > 0 ? d.tags.join(', ') : '—'}
          </span>
        ),
      },
      tz: {
        id: 'tz',
        labelKey: 'Timezone',
        render: (d) => <span className="text-sm">{d.tz ?? '—'}</span>,
      },
      activationDate: {
        id: 'activationDate',
        labelKey: 'Paired',
        sortField: 'creationDate',
        render: (d) => (
          <span className="text-sm text-muted-foreground">
            {formatDateMedium(d.activationDate, '—')}
          </span>
        ),
      },
    }),
    [t],
  );

  // Build visible column list in canonical order, pinning status + name.
  const visibleOrderIds = useMemo(() => {
    const pinnedLead = ['deviceName'];
    const visibleSet = new Set(columnConfig.visible);
    // Respect canonical column order, drop status & deviceName (pinned elsewhere).
    const optional = columnConfig.order.filter(
      (id) => id !== 'status' && id !== 'deviceName' && visibleSet.has(id),
    );
    return [...pinnedLead, ...optional];
  }, [columnConfig]);

  const handleHeaderClick = (field: MdmDeviceSortField | undefined) => {
    if (!field) return;
    if (sortField === field) {
      setSort(
        field,
        sortDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
      );
    } else {
      setSort(field, SORT_DIRECTION.DESC);
    }
  };

  const renderSortIcon = (field: MdmDeviceSortField | undefined) => {
    if (!field) return null;
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" aria-hidden />;
    return sortDirection === SORT_DIRECTION.ASC ? (
      <ArrowUp className="ml-1 h-3 w-3" aria-hidden />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" aria-hidden />
    );
  };

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <Table data-testid="mdm-devices-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                aria-label={t('Select all')}
                checked={allSelected}
                onCheckedChange={(checked) =>
                  checked ? selectAll(devices.map((d) => d._id)) : clear()
                }
              />
            </TableHead>
            <TableHead className="w-10">{t('Status')}</TableHead>
            {visibleOrderIds.map((id) => {
              const col = columnMap[id];
              if (!col) return null;
              return (
                <TableHead key={id}>
                  {col.sortField ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleHeaderClick(col.sortField)}
                      className="h-auto -ml-2 cursor-pointer"
                    >
                      {t(col.labelKey)}
                      {renderSortIcon(col.sortField)}
                    </Button>
                  ) : (
                    t(col.labelKey)
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => {
            const checked = selectedIds.has(device._id);
            return (
              <TableRow
                key={device._id}
                data-testid="mdm-device-row"
                className={cn(
                  'cursor-pointer hover:bg-accent/50 transition-colors',
                  checked && 'bg-accent/30',
                )}
                onClick={() => onRowClick(device._id)}
              >
                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    aria-label={t('Select row')}
                    checked={checked}
                    onCheckedChange={() => toggle(device._id)}
                  />
                </TableCell>
                <TableCell className="w-10">
                  <DeviceStatusDot device={device} />
                </TableCell>
                {visibleOrderIds.map((id) => {
                  const col = columnMap[id];
                  if (!col) return null;
                  return <TableCell key={id}>{col.render(device)}</TableCell>;
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
